/**
 * Loadout context — tracks "what's on the car right now" (roof rack, roof box).
 * Mirrors app/src/contexts/LoadoutContext.tsx.
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import type { Car } from '../types';
import { useAuth } from './AuthContext';

const ROOF_RACK_HEIGHT_M = 0.1;
const ROOF_BOX_HEIGHT_M = 0.4;

interface LoadoutState {
  roofRack: boolean;
  roofBox: boolean;
}

interface LoadoutContextValue {
  loadout: LoadoutState;
  toggleRoofRack: () => void;
  toggleRoofBox: () => void;
  extraHeightM: number;
  effectiveHeight: (car: Car) => number;
}

const LoadoutContext = createContext<LoadoutContextValue | null>(null);

export const LoadoutProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { effectiveCars } = useAuth();
  const activeCar = effectiveCars[0];
  const [loadout, setLoadout] = useState<LoadoutState>({
    roofRack: !!activeCar?.hasRoofRack,
    roofBox: !!activeCar?.hasRoofBox,
  });

  useEffect(() => {
    setLoadout({
      roofRack: !!activeCar?.hasRoofRack,
      roofBox: !!activeCar?.hasRoofBox,
    });
  }, [activeCar?.id, activeCar?.hasRoofRack, activeCar?.hasRoofBox]);

  const toggleRoofRack = useCallback(
    () => setLoadout((prev) => ({ ...prev, roofRack: !prev.roofRack })),
    []
  );
  const toggleRoofBox = useCallback(
    () => setLoadout((prev) => ({ ...prev, roofBox: !prev.roofBox })),
    []
  );

  const extraHeightM =
    (loadout.roofRack ? ROOF_RACK_HEIGHT_M : 0) +
    (loadout.roofBox ? ROOF_BOX_HEIGHT_M : 0);

  const effectiveHeight = useCallback(
    (car: Car) => +(car.heightM + extraHeightM).toFixed(2),
    [extraHeightM]
  );

  const value = useMemo(
    () => ({ loadout, toggleRoofRack, toggleRoofBox, extraHeightM, effectiveHeight }),
    [loadout, toggleRoofRack, toggleRoofBox, extraHeightM, effectiveHeight]
  );

  return <LoadoutContext.Provider value={value}>{children}</LoadoutContext.Provider>;
};

export const useLoadout = (): LoadoutContextValue => {
  const ctx = useContext(LoadoutContext);
  if (!ctx) throw new Error('useLoadout must be inside LoadoutProvider');
  return ctx;
};

export const LOADOUT_DEFAULTS = {
  roofRack: ROOF_RACK_HEIGHT_M,
  roofBox: ROOF_BOX_HEIGHT_M,
};
